import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import {
  Search, ScanBarcode, ArrowRight, Fuel, ShoppingCart, BarChart3,
  Star, Check, Menu, X, MapPin, Scissors, SprayCan, Bell, Heart,
  Shield, Users, User, TrendingDown, ChevronRight, Zap, Award, Clock,
  Send, ChefHat
} from "lucide-react";
import FounderUrgencyWidget from "../components/FounderUrgencyWidget";
import LiveFuelCoverageWidget from "../components/LiveFuelCoverageWidget";
import Analytics from "../utils/analytics";
import { trackLandingView, trackGetStartedClick } from "../utils/tracking";

const API = process.env.REACT_APP_BACKEND_URL;

const NAV_LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Features", href: "#features" },
  { label: "Fuel", href: "#fuel" },
  { label: "Community", href: "/community", isRoute: true },
  { label: "Pricing", href: "/pricing", isRoute: true },
  { label: "Blog", href: "/blog", isRoute: true },
];

function Anim({ children, className = "", delay = 0 }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

function AnimScale({ children, className = "", delay = 0 }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

function PhoneMockup() {
  return (
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      className="rounded-[2rem] border-[5px] border-zinc-800 bg-zinc-50 overflow-hidden shadow-2xl aspect-[9/19] w-full max-w-[260px] mx-auto"
    >
      <div className="bg-zinc-800 h-6 flex items-center justify-between px-4">
        <span className="text-[9px] text-white/60 font-medium" style={{ fontFamily: "Manrope,sans-serif" }}>9:41</span>
        <div className="w-3 h-1.5 border border-white/40 rounded-sm" />
      </div>
      <div className="bg-white px-3 py-2 border-b border-zinc-100 flex items-center gap-1.5">
        <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center"><span className="text-white text-[7px] font-black" style={{ fontFamily: "Outfit,sans-serif" }}>P</span></div>
        <span className="text-[10px] font-bold text-zinc-800" style={{ fontFamily: "Outfit,sans-serif" }}>Compare Prices</span>
      </div>
      <div className="px-2.5 pt-2.5 pb-1.5">
        <div className="bg-zinc-100 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5">
          <Search className="w-2.5 h-2.5 text-zinc-400" />
          <span className="text-[9px] text-zinc-400">Milk 1L</span>
        </div>
      </div>
      <div className="mx-2.5 bg-emerald-600 rounded-lg p-2 text-center mb-1.5">
        <p className="text-[7px] text-emerald-200 font-medium uppercase tracking-wider">You could save</p>
        <p className="text-lg font-black text-white leading-tight" style={{ fontFamily: "Outfit,sans-serif" }}>£0.45</p>
      </div>
      <div className="mx-2.5 bg-white rounded-lg border border-zinc-100 overflow-hidden">
        {[
          { name: "Asda", price: "£1.00", best: true },
          { name: "Tesco", price: "£1.20", extra: "+£0.20" },
          { name: "Sainsbury's", price: "£1.30", extra: "+£0.30" },
          { name: "Morrisons", price: "£1.45", extra: "+£0.45" },
        ].map((r, i) => (
          <div key={r.name} className={`flex items-center gap-1.5 px-2 py-1.5 ${i < 3 ? "border-b border-zinc-50" : ""} ${r.best ? "bg-emerald-50" : ""}`}>
            <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7px] font-bold ${r.best ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-500"}`}>{i + 1}</div>
            <div className="flex-1">
              <span className="text-[9px] font-semibold text-zinc-800">{r.name}</span>
              {r.best && <span className="ml-1 bg-emerald-600 text-white text-[5px] font-bold px-1 py-0.5 rounded">BEST</span>}
            </div>
            <div className="text-right">
              <span className={`text-[10px] font-bold ${r.best ? "text-emerald-700" : "text-zinc-800"}`}>{r.price}</span>
              {r.extra && <p className="text-[6px] text-red-500 font-medium">{r.extra}</p>}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function CountUp({ end, suffix = "", prefix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

const CURATED_SAVINGS = [
  { product: "Hovis Wholemeal Bread 800g", saving: "£0.45", cheapest: "Aldi", vs: "Tesco" },
  { product: "Fairy Liquid Original 900ml", saving: "£1.20", cheapest: "Asda", vs: "Waitrose" },
  { product: "Unleaded Petrol", saving: "5p/L", cheapest: "Morrisons Peckham", vs: "avg" },
  { product: "Diesel", saving: "4p/L", cheapest: "Sainsbury's Local", vs: "avg" },
];

function SavingsTicker() {
  const [items, setItems] = useState(CURATED_SAVINGS);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/public/savings-ticker`)
      .then(r => r.json())
      .then(data => {
        if (data?.items?.length > 0) {
          const fuelItems = CURATED_SAVINGS.filter(s => s.vs === "avg");
          const merged = [...data.items, ...fuelItems];
          setItems(merged);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % items.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [items.length]);

  const item = items[currentIndex];
  const isFuel = item.vs === "avg";

  return (
    <div className="min-h-8 py-1 overflow-hidden relative" data-testid="savings-ticker">
      <motion.div
        key={currentIndex}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="flex items-center justify-center gap-2 text-xs sm:text-sm flex-wrap px-2"
      >
        <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-full text-xs whitespace-nowrap">
          <TrendingDown className="w-3 h-3" /> {item.saving}
        </span>
        <span className="text-zinc-500 text-center">
          <strong className="text-zinc-700">{item.product}</strong>
          {isFuel
            ? <> — cheaper at <strong className="text-zinc-700">{item.cheapest}</strong> vs {item.vs}</>
            : <> — <strong className="text-zinc-700">{item.cheapest}</strong> vs {item.vs}</>
          }
        </span>
      </motion.div>
    </div>
  );
}

export default function Homepage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [fuelPrices, setFuelPrices] = useState(null);
  const [landingStats, setLandingStats] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState("");

  useEffect(() => {
    // 📊 Iter141c — Top-of-funnel visibility for Add People campaign
    // 1. Capture first-touch attribution (UTM + referrer) — persistent
    Analytics.captureFirstTouchAttribution();
    // 2. Fire page_view event so admin can measure landing-page visits
    Analytics.trackPageView('homepage', { section: 'landing' });

    fetch(`${API}/api/fuel/prices/live`)
      .then(r => r.json())
      .then(data => { if (data?.fuel_types) setFuelPrices(data); })
      .catch(() => {});

    fetch(`${API}/api/public/landing-stats`)
      .then(r => r.json())
      .then(data => setLandingStats(data))
      .catch(() => setLandingStats({ users: 35, products: 7000, comparisons: 500, total_savings: 4250, fuel_stations: 8500, supermarkets: 6 }));
  }, []);

  // 📊 CTA click helper — attach as onClick on every "Start saving" / "Get started" link
  const handleCtaClick = React.useCallback((ctaName, extra = {}) => {
    Analytics.trackCtaClick(ctaName, extra);
    // iter158 — Also push a dedicated `get_started_click` event to the
    // dataLayer so GTM tags fire immediately. This is REPORTING-ONLY and
    // must not be used as a Google Ads optimisation goal.
    trackGetStartedClick(extra.position || ctaName, { label: extra.label, cta: ctaName });
  }, []);

  // iter158 — Landing-page view event. Captures first-touch UTM/gclid,
  // new-vs-returning flag, and device breakdown for the Marketing Dashboard.
  useEffect(() => {
    trackLandingView('homepage');
  }, []);

  const liveUnleaded = fuelPrices?.fuel_types?.unleaded?.price ?? 145.8;
  const livePremium = fuelPrices?.fuel_types?.super_unleaded?.price ?? 164.2;
  const liveDiesel = fuelPrices?.fuel_types?.diesel?.price ?? 169.5;
  // Iter171 spec-consistency fix: source the public "Fuel stations" claim from
  // /api/public/landing-stats (single source of truth) so it matches the docs
  // and never contradicts other public-facing counts. Falls back to the live
  // fuel feed's station_count only if landing-stats hasn't returned yet.
  const stationCount = landingStats?.fuel_stations ?? fuelPrices?.station_count ?? 3023;
  const isLive = !!fuelPrices;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterStatus("sending");
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newsletterEmail.trim(), source: "landing_page" })
      });
      if (res.ok) {
        setNewsletterStatus("success");
        setNewsletterEmail("");
      } else {
        setNewsletterStatus("error");
      }
    } catch {
      setNewsletterStatus("error");
    }
    setTimeout(() => setNewsletterStatus(""), 4000);
  };

  return (
    <div style={{ fontFamily: "Manrope,sans-serif" }} className="bg-[#FAFAFA] text-zinc-900 overflow-x-hidden">
      <Helmet>
        <title>PricePAL Compare | UK's Smartest Grocery & Fuel Price Comparison — Premium £1.99/mo</title>
        <meta name="description" content="Compare prices in seconds while you shop. Scan or search any product across UK supermarkets and find cheap fuel nearby. Start Premium from £1.99/month — 7-day free trial, cancel anytime." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "PricePAL Compare",
          "description": "UK price comparison app for groceries and fuel",
          "url": "https://pricepalcompare.com",
          "applicationCategory": "ShoppingApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "GBP" }
        })}</script>
      </Helmet>

      {/* ── HEADER ── */}
      <header className="fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="header-logo">
            <div className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center"><span className="text-white font-black text-xs" style={{ fontFamily: "Outfit,sans-serif" }}>P</span></div>
            <span className="text-base font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}><span className="hidden sm:inline">PricePAL Compare</span><span className="sm:hidden">PricePAL</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 mx-auto">
            {NAV_LINKS.map(l => (
              l.href.startsWith("#")
                ? <a key={l.href} href={l.href} className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition">{l.label}</a>
                : <Link key={l.href} to={l.href} className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition">{l.label}</Link>
            ))}
          </nav>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to="/app" onClick={() => handleCtaClick('nav_login', { position: 'header' })} className="hidden sm:inline-flex text-sm font-medium text-zinc-500 hover:text-emerald-600 transition">Log in</Link>
            <Link to="/register" data-testid="header-cta" onClick={() => handleCtaClick('header_cta', { position: 'header', label: 'Get started' })} className="bg-emerald-600 text-white hover:bg-emerald-700 font-bold px-4 py-2 rounded-full text-sm transition flex items-center gap-1.5 shadow-sm">
              Get started <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <button className="md:hidden p-1.5 text-zinc-600" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-toggle" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="mobile-menu-overlay">
          <div className="flex items-center justify-between px-4 h-14 border-b border-zinc-100">
            <span className="text-base font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>PricePAL Compare</span>
            <button onClick={() => setMenuOpen(false)} className="p-1.5 text-zinc-600" aria-label="Close menu"><X className="w-5 h-5" /></button>
          </div>
          <nav className="flex-1 flex flex-col justify-center px-6 gap-6">
            {NAV_LINKS.map(l => (
              l.href.startsWith("#")
                ? <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="text-2xl font-bold text-zinc-800 hover:text-emerald-600 transition" style={{ fontFamily: "Outfit,sans-serif" }}>{l.label}</a>
                : <Link key={l.href} to={l.href} onClick={() => setMenuOpen(false)} className="text-2xl font-bold text-zinc-800 hover:text-emerald-600 transition" style={{ fontFamily: "Outfit,sans-serif" }}>{l.label}</Link>
            ))}
          </nav>
          <div className="px-6 pb-8">
            <Link to="/register" onClick={() => { setMenuOpen(false); handleCtaClick('mobile_menu_cta', { position: 'mobile_menu' }); }} data-testid="mobile-menu-cta" className="block w-full bg-emerald-600 text-white font-bold py-4 rounded-full text-center text-base">Start 7-Day Trial</Link>
          </div>
        </div>
      )}

      <div className="h-14" />

      {/* ── S1: HERO ── */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-10 pb-8 md:pt-20 md:pb-16 overflow-hidden">
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #000 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        {/* Gradient accent */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-100/40 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <Anim>
                <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
                  <Zap className="w-3 h-3" /> 7 days free · Card required · Cancel anytime
                </div>
              </Anim>
              <Anim delay={0.04}>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl tracking-tight font-black text-zinc-900 leading-[1.08]" style={{ fontFamily: "Outfit,sans-serif" }}>
                  Find cheaper groceries & fuel near you <span className="text-emerald-600">in seconds</span>
                </h1>
              </Anim>
              <Anim delay={0.08}>
                <p className="mt-4 text-base sm:text-lg text-zinc-500 leading-relaxed max-w-lg">
                  Compare supermarket prices while you shop. Scan a barcode or search for a product, find cheaper fuel nearby and see where your money goes further.
                </p>
              </Anim>
              <Anim delay={0.12}>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link to="/register" data-testid="hero-cta" onClick={() => handleCtaClick('hero_cta', { position: 'hero', label: 'Start 7-Day Free Trial' })} className="inline-flex items-center justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 font-bold px-8 py-4 rounded-full text-base text-center shadow-[0_8px_30px_rgb(5,150,105,0.3)] hover:-translate-y-0.5 transition-all">
                    Start 7-Day Free Trial <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href="#how-it-works" className="inline-flex items-center justify-center gap-2 bg-white text-zinc-700 hover:text-emerald-600 font-semibold px-6 py-4 rounded-full text-base border border-zinc-200 hover:border-emerald-200 transition-all">
                    See how it works
                  </a>
                </div>
                <p className="mt-3 text-sm text-zinc-600 font-semibold leading-snug max-w-lg" data-testid="hero-cta-support-primary">Premium £1.99/month after your trial</p>
                <p className="mt-1 text-xs text-zinc-500 leading-snug max-w-lg" data-testid="hero-cta-support-line">Card required · No charge today · Cancel before your trial ends and you won't be charged.</p>
                <p className="mt-2 text-xs text-zinc-600 leading-snug max-w-lg" data-testid="hero-family-secondary">Family £2.99/month after the 7-day free trial · Up to 4 members</p>
              </Anim>
              {/* Live stats */}
              <Anim delay={0.2}>
                <div className="mt-8 flex flex-wrap gap-4 sm:gap-6">
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl font-black text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>
                      <CountUp end={landingStats?.products || 7000} suffix="+" />
                    </p>
                    <p className="text-[11px] text-zinc-400 font-medium">Products tracked</p>
                  </div>
                  <div className="w-px bg-zinc-200 hidden sm:block" />
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl font-black text-emerald-600" style={{ fontFamily: "Outfit,sans-serif" }}>
                      <CountUp end={stationCount} suffix="+" />
                    </p>
                    <p className="text-[11px] text-zinc-400 font-medium">Fuel stations</p>
                  </div>
                  <div className="w-px bg-zinc-200 hidden sm:block" />
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl font-black text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>
                      {landingStats?.supermarkets ?? 6}
                    </p>
                    <p className="text-[11px] text-zinc-400 font-medium">Supermarkets compared</p>
                  </div>
                </div>
              </Anim>
            </div>
            <Anim delay={0.12} className="flex justify-center">
              <PhoneMockup />
            </Anim>
          </div>
        </div>
      </section>

      {/* ── S1.5: FOUNDER URGENCY WIDGET — HIDDEN 2026-06 per user directive
          (beta tiers no longer surfaced on public pages). Kept import + JSX
          commented to make future re-enable a one-line revert. ──
      <FounderUrgencyWidget />
      */}

      {/* ── S2: TRUST BAR ── */}
      <section className="border-y border-zinc-100 bg-white py-5" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-[10px] text-zinc-400 font-semibold tracking-[0.15em] uppercase mb-4">Compare prices across major UK supermarkets</p>
          <div className="flex gap-3 sm:gap-8 justify-center flex-wrap items-center">
            {[
              { name: "TESCO", color: "#00539F" },
              { name: "ASDA", color: "#78BE20" },
              { name: "SAINSBURY'S", color: "#F06C00" },
              { name: "MORRISONS", color: "#007A3D" },
              { name: "ALDI", color: "#00205B" },
              { name: "LIDL", color: "#0050AA" },
              { name: "WAITROSE", color: "#5E7E3E" },
              { name: "CO-OP", color: "#00B1E7" },
            ].map(store => (
              <div key={store.name} className="flex items-center gap-1.5 flex-shrink-0">
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: store.color }} />
                <span className="text-zinc-400 font-extrabold tracking-[0.08em] text-[11px] sm:text-sm whitespace-nowrap select-none" style={{ fontFamily: "Outfit,sans-serif" }}>{store.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SAVINGS TICKER ── */}
      <section className="py-3 bg-[#FAFAFA]" data-testid="savings-ticker-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2">
            <span className="hidden sm:inline text-[10px] text-zinc-400 font-semibold uppercase tracking-wider flex-shrink-0">Saving found</span>
            <span className="hidden sm:inline text-zinc-200">|</span>
            <SavingsTicker />
          </div>
        </div>
      </section>

      {/* ── S3: HOW IT WORKS ── */}
      <section id="how-it-works" className="px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="how-it-works">
        <div className="max-w-5xl mx-auto">
          <Anim className="text-center mb-10">
            <p className="text-[11px] text-emerald-600 font-semibold uppercase tracking-wider mb-2">Simple & fast</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>How PricePAL Compare works</h2>
          </Anim>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <ScanBarcode className="w-6 h-6 text-emerald-600" />, title: "Scan or search", desc: "Scan a barcode or type any product name" },
              { icon: <BarChart3 className="w-6 h-6 text-emerald-600" />, title: "Compare instantly", desc: "Compare available prices across supported UK supermarkets" },
              { icon: <TrendingDown className="w-6 h-6 text-emerald-600" />, title: "Find cheapest", desc: "See where the products you're comparing cost less" },
              { icon: <Bell className="w-6 h-6 text-emerald-600" />, title: "Get alerts", desc: "We notify you when prices drop" },
            ].map((s, i) => (
              <Anim key={s.title} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100 relative overflow-hidden hover:shadow-md hover:border-emerald-100 transition-all duration-300 group">
                  <span className="absolute -top-3 -right-3 text-emerald-50 text-7xl font-black select-none pointer-events-none group-hover:text-emerald-100/60 transition-colors" style={{ fontFamily: "Outfit,sans-serif" }}>{i + 1}</span>
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mb-3 group-hover:bg-emerald-100 transition-colors">{s.icon}</div>
                  <h3 className="text-base font-bold text-zinc-900 mb-0.5" style={{ fontFamily: "Outfit,sans-serif" }}>{s.title}</h3>
                  <p className="text-sm text-zinc-500 leading-snug">{s.desc}</p>
                </div>
              </Anim>
            ))}
          </div>
        </div>
      </section>

      {/* ── S4: VALUE PROP + SCAN MOCKUP ── */}
      <section className="px-4 sm:px-6 lg:px-8 py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <Anim className="flex justify-center">
              <div className="bg-zinc-800 rounded-[2rem] p-1 shadow-2xl w-full max-w-[300px]">
                <div className="bg-white rounded-[1.5rem] overflow-hidden">
                  <div className="bg-emerald-600 px-4 py-2.5 flex items-center gap-2">
                    <ScanBarcode className="w-4 h-4 text-white" />
                    <span className="text-sm font-bold text-white" style={{ fontFamily: "Outfit,sans-serif" }}>Scan Result</span>
                  </div>
                  <div className="px-3.5 pt-3.5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0 border border-amber-100">
                        <ShoppingCart className="w-6 h-6 text-amber-500" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-zinc-800" style={{ fontFamily: "Outfit,sans-serif" }}>Weetabix Original 24pk</p>
                        <p className="text-[10px] text-zinc-400">Barcode: 5010029000078</p>
                        <div className="flex items-center gap-1 mt-1">
                          <span className="bg-emerald-100 text-emerald-700 text-[8px] font-bold px-1.5 py-0.5 rounded">A</span>
                          <span className="text-[9px] text-zinc-400">Nutri-Score</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-emerald-600 rounded-xl p-2.5 text-center mb-3">
                      <p className="text-[8px] text-emerald-200 font-medium uppercase tracking-wider">You could save up to</p>
                      <p className="text-xl font-black text-white" style={{ fontFamily: "Outfit,sans-serif" }}>£1.10</p>
                    </div>
                    <div className="rounded-xl border border-zinc-100 overflow-hidden mb-3">
                      {[
                        { rank: 1, name: "Aldi", price: "£2.49", best: true },
                        { rank: 2, name: "Asda", price: "£2.75", extra: "+£0.26" },
                        { rank: 3, name: "Tesco", price: "£3.00", extra: "+£0.51" },
                        { rank: 4, name: "Sainsbury's", price: "£3.15", extra: "+£0.66" },
                        { rank: 5, name: "Waitrose", price: "£3.59", extra: "+£1.10" },
                      ].map((r, i) => (
                        <div key={r.name} className={`flex items-center gap-2 px-3 py-2 ${i < 4 ? "border-b border-zinc-50" : ""} ${r.best ? "bg-emerald-50" : ""}`}>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${r.best ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-500"}`}>{r.rank}</div>
                          <div className="flex-1">
                            <span className="text-[11px] font-semibold text-zinc-800">{r.name}</span>
                            {r.best && <span className="ml-1.5 bg-emerald-600 text-white text-[7px] font-bold px-1.5 py-0.5 rounded">BEST DEAL</span>}
                          </div>
                          <div className="text-right">
                            <span className={`text-xs font-bold ${r.best ? "text-emerald-700" : "text-zinc-800"}`}>{r.price}</span>
                            {r.extra && <p className="text-[8px] text-red-500 font-medium">{r.extra}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3.5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-bold text-amber-800">Price Alert Set</p>
                          <p className="text-[9px] text-amber-600">Notify me when below £2.40</p>
                        </div>
                        <div className="w-8 h-5 bg-emerald-500 rounded-full relative">
                          <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Anim>
            <Anim delay={0.1}>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-zinc-900 mb-3" style={{ fontFamily: "Outfit,sans-serif" }}>Stop overpaying for everyday shopping</h2>
              <p className="text-sm sm:text-base text-zinc-500 leading-relaxed mb-5">Prices can vary significantly between retailers. PricePAL Compare brings together retailer, fuel and community price data to help you quickly compare your options.</p>
              <ul className="space-y-3">
                {[
                  { text: "Compare across supported UK supermarkets", sub: "Tesco, Asda, Sainsbury's, Aldi, Lidl and others" },
                  { text: "Retailer and community price data", sub: "Compare available prices from multiple sources" },
                  { text: "Set price alerts for your favourites", sub: "Get notified when prices drop" },
                  { text: "Spot potential savings on your shop", sub: "Compare prices before you buy and see where products cost less" },
                ].map(item => (
                  <li key={item.text} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-emerald-600" /></div>
                    <div>
                      <span className="text-sm text-zinc-800 font-semibold">{item.text}</span>
                      <p className="text-xs text-zinc-400">{item.sub}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Anim>
          </div>
        </div>
      </section>

      {/* ── S5: FEATURES GRID ── */}
      <section id="features" className="px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="features-section">
        <div className="max-w-5xl mx-auto">
          <Anim className="text-center mb-10">
            <p className="text-[11px] text-emerald-600 font-semibold uppercase tracking-wider mb-2">Packed with tools</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>Everything you need while shopping</h2>
          </Anim>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: <ScanBarcode className="w-5 h-5" />, title: "Barcode Scanner", desc: "Scan a product barcode to quickly compare available prices", accent: "bg-blue-50 text-blue-600 border-blue-100" },
              { icon: <Fuel className="w-5 h-5" />, title: "Fuel Price Finder", desc: "Find and compare current petrol and diesel prices near you", accent: "bg-orange-50 text-orange-600 border-orange-100" },
              { icon: <Bell className="w-5 h-5" />, title: "Price Drop Alerts", desc: "Get notified when products you're watching drop in price", accent: "bg-amber-50 text-amber-600 border-amber-100" },
              { icon: <ChefHat className="w-5 h-5" />, title: "Kitchen AI", desc: "Get recipe ideas using PricePAL's Kitchen AI", accent: "bg-emerald-50 text-emerald-600 border-emerald-100" },
              { icon: <Heart className="w-5 h-5" />, title: "NHS & Blue Light", desc: "Dedicated community pricing and benefits for eligible NHS and Blue Light members", accent: "bg-indigo-50 text-indigo-600 border-indigo-100" },
            ].map((f, i) => (
              <AnimScale key={f.title} delay={i * 0.06}>
                <div className={`rounded-2xl border p-5 hover:shadow-md transition-all duration-300 cursor-default ${f.accent}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${f.accent}`}>{f.icon}</div>
                    <h3 className="text-sm font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>{f.title}</h3>
                  </div>
                  <p className="text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
                </div>
              </AnimScale>
            ))}
          </div>
        </div>
      </section>

      {/* ── S6: CATEGORIES STRIP ── */}
      <section className="px-4 sm:px-6 lg:px-8 py-10 bg-white" data-testid="categories-section">
        <div className="max-w-5xl mx-auto">
          <Anim className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl tracking-tight font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>Compare more than just groceries</h2>
          </Anim>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: <ShoppingCart className="w-5 h-5" />, label: "Groceries & household", examples: "Milk, bread, cereal, cleaning products", accent: "bg-emerald-50 text-emerald-600 border-emerald-100" },
              { icon: <Fuel className="w-5 h-5" />, label: "Fuel & personal care", examples: "Petrol, diesel, shampoo, skincare, deodorant", accent: "bg-orange-50 text-orange-600 border-orange-100" },
            ].map((c, i) => (
              <Anim key={c.label} delay={i * 0.08}>
                <div className={`rounded-xl border p-4 text-center hover:shadow-md transition-all duration-300 ${c.accent}`}>
                  <div className="flex justify-center mb-2">{c.icon}</div>
                  <p className="text-sm font-semibold text-zinc-800 mb-1">{c.label}</p>
                  <p className="text-[11px] text-zinc-400">{c.examples}</p>
                </div>
              </Anim>
            ))}
          </div>
        </div>
      </section>

      {/* ── S6.5: FAMILY PLAN VALUE (iter172f) ── */}
      <section className="px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="family-value-section">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-amber-50 via-white to-emerald-50 border border-amber-100 p-6 md:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <Anim>
                <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-900 text-xs font-semibold px-3 py-1 rounded-full mb-4 border border-amber-200">
                  <Users className="w-3 h-3" /> Family Plan · £2.99/month
                </div>
                <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-zinc-900 mb-4" style={{ fontFamily: "Outfit,sans-serif" }}>
                  One bill. Four shoppers. <span className="text-emerald-600">One household saving together.</span>
                </h2>
                <p className="text-zinc-600 mb-6 text-sm sm:text-base">
                  Every adult in the family gets their own login and profile — but all your shopping lists, price alerts and savings roll up to one shared household view. Nothing gets bought twice. Everyone sees the deal.
                </p>
                <div className="rounded-2xl bg-white border border-zinc-100 p-4 mb-6">
                  <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mb-2">The maths</p>
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-black text-emerald-600" style={{ fontFamily: "Outfit,sans-serif" }}>75p</span>
                    <span className="text-sm text-zinc-500">per family member per month</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    vs. £7.96/mo for four solo Premium subs — <strong className="text-zinc-800">save £4.97 every month</strong>.
                  </p>
                </div>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-full text-sm transition shadow-sm"
                  data-testid="family-cta"
                >
                  Start 7-Day Family Trial <ArrowRight className="w-4 h-4" />
                </Link>
                <p className="text-[11px] text-zinc-400 mt-3">Card required · No charge today · Cancel any time</p>
              </Anim>

              <Anim delay={0.15}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: <ShoppingCart className="w-5 h-5" />, title: "Shared shopping lists", desc: "Every phone. Same list. Real-time sync so bread never gets bought twice.", accent: "bg-emerald-50 border-emerald-100 text-emerald-700" },
                    { icon: <ChefHat className="w-5 h-5" />, title: "Household Kitchen AI", desc: "Recipes auto-scale to the number and ages of your family — no more \"cook for two\" leftovers.", accent: "bg-orange-50 border-orange-100 text-orange-700" },
                    { icon: <TrendingDown className="w-5 h-5" />, title: "Combined savings tracker", desc: "See the whole household's monthly savings in one place. Kids' snacks, teens' shampoo, adults' fuel — all counted.", accent: "bg-blue-50 border-blue-100 text-blue-700" },
                    { icon: <User className="w-5 h-5" />, title: "Individual logins", desc: "Each family member has their own account and profile — no password-sharing, no mixed alerts.", accent: "bg-purple-50 border-purple-100 text-purple-700" },
                    { icon: <Bell className="w-5 h-5" />, title: "Household price alerts", desc: "Set an alert once. Any family member who spots the deal in-store gets pinged.", accent: "bg-amber-50 border-amber-100 text-amber-700" },
                    { icon: <Shield className="w-5 h-5" />, title: "Parental controls", desc: "Add children as viewer-only members. They can build lists but can't change household settings.", accent: "bg-rose-50 border-rose-100 text-rose-700" },
                  ].map((f, i) => (
                    <div key={f.title} className={`rounded-xl border p-4 ${f.accent}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-white/70 flex items-center justify-center">{f.icon}</div>
                        <h3 className="text-sm font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>{f.title}</h3>
                      </div>
                      <p className="text-[11px] text-zinc-600 leading-relaxed">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </Anim>
            </div>
          </div>
        </div>
      </section>

      {/* ── S7: FUEL ── */}
      {/* ── Live fuel coverage trust strip ── */}
      <LiveFuelCoverageWidget />

      <section id="fuel" className="px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-white" style={{ backgroundColor: "#022c22" }} data-testid="fuel-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <Anim>
              <div className="inline-flex items-center gap-1.5 bg-emerald-900/50 text-emerald-400 text-xs font-semibold px-3 py-1 rounded-full mb-4 border border-emerald-800">
                <Fuel className="w-3 h-3" /> Fuel Finder
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold leading-tight mb-3" style={{ fontFamily: "Outfit,sans-serif" }}>Compare fuel prices near you</h2>
              <p className="text-sm sm:text-base text-emerald-200/60 leading-relaxed mb-5">Compare current petrol and diesel prices at supported UK stations to find where fuel is cheaper on your route.</p>
              <ul className="space-y-2.5 mb-6">
                {[
                  "See nearby stations with current prices",
                  "Prices refreshed regularly from official station feeds",
                  "Compare unleaded, diesel and premium fuels",
                  "Find a cheaper station on your route"
                ].map(item => (
                  <li key={item} className="flex items-center gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-emerald-400" /></div>
                    <span className="text-sm text-emerald-100/80 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/app?view=fuel-prices" data-testid="fuel-cta" className="inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 font-bold px-5 py-3 rounded-full text-sm transition shadow-sm">
                View fuel prices <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </Anim>
            <Anim delay={0.12}>
              <div className="bg-emerald-950/60 rounded-2xl p-1 shadow-2xl max-w-sm mx-auto border border-emerald-800/30">
                <div className="bg-white rounded-[0.875rem] overflow-hidden">
                  <div className="bg-emerald-600 px-3.5 py-2.5 flex items-center gap-1.5">
                    <Fuel className="w-3.5 h-3.5 text-white" />
                    <span className="text-xs font-bold text-white" style={{ fontFamily: "Outfit,sans-serif" }}>UK Live Fuel Prices</span>
                    <span className="ml-auto bg-emerald-500 text-[8px] text-white font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1">
                      <span className={`w-1 h-1 rounded-full ${isLive ? "bg-white animate-pulse" : "bg-white/50"}`} />{isLive ? "LIVE" : "..."}
                    </span>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-[9px] text-zinc-400 mb-2" data-testid="fuel-station-count">Current averages from {stationCount.toLocaleString()} UK stations</p>
                    <div className="grid grid-cols-3 gap-1.5 mb-2.5">
                      {[
                        { label: "Unleaded", price: liveUnleaded.toFixed(1), bg: "bg-emerald-50" },
                        { label: "Premium", price: livePremium.toFixed(1), bg: "bg-purple-50" },
                        { label: "Diesel", price: liveDiesel.toFixed(1), bg: "bg-blue-50" },
                      ].map(f => (
                        <div key={f.label} className={`${f.bg} rounded-lg p-1.5 text-center`} data-testid={`fuel-price-${f.label.toLowerCase()}`}>
                          <p className="text-[7px] text-zinc-500 font-semibold uppercase">{f.label}</p>
                          <p className="text-sm font-black text-zinc-800">{f.price}<span className="text-[8px] font-medium">p/L</span></p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-2.5 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-1 mb-0.5"><MapPin className="w-2.5 h-2.5 text-white/80" /><span className="text-[8px] text-white/80 font-medium">1.98km away</span></div>
                        <p className="text-xs font-black text-white" style={{ fontFamily: "Outfit,sans-serif" }}>Cheapest nearby</p>
                        <p className="text-base font-black text-white">140.7<span className="text-[9px] font-medium">p/L</span></p>
                      </div>
                      <div className="text-right">
                        <div className="inline-flex items-center gap-1 bg-white/20 border border-white/30 rounded-full px-2 py-0.5">
                          <Fuel className="w-2.5 h-2.5 text-white" />
                          <span className="text-[9px] font-bold text-white uppercase tracking-wider">Best price</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Anim>
          </div>
        </div>
      </section>

      {/* ── S9: NHS & BLUE LIGHT ── */}
      <section id="nhs" className="px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="nhs-section">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden" style={{ background: "linear-gradient(135deg, #003087 0%, #005EB8 50%, #0072CE 100%)" }}>
            {/* Decorative pattern */}
            <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center p-6 sm:p-10 lg:p-14">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-4 border border-white/20 backdrop-blur-sm">
                  <Shield className="w-3 h-3" /> NHS & Blue Light Card
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white leading-tight mb-3" style={{ fontFamily: "Outfit,sans-serif" }}>
                  Community pricing for NHS &amp; Blue Light workers
                </h2>
                <p className="text-sm sm:text-base text-blue-100/80 leading-relaxed mb-5">
                  Eligible NHS staff, paramedics, police, fire service workers, students and teachers can subscribe on our reduced Community rate of <strong>£1.00/month</strong> once ID is verified. It's our way of saying thank you for keeping the country running.
                </p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    "Community rate £1.00/month (subject to verification)",
                    "Same features as Premium — reduced monthly rate",
                    "Reduced rate locked in for as long as you remain subscribed"
                  ].map(item => (
                    <li key={item} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-white" /></div>
                      <span className="text-sm text-blue-50/90 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/community" data-testid="community-cta" className="inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-blue-50 font-bold px-6 py-3 rounded-full text-sm transition shadow-lg">
                  Verify eligibility & subscribe <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <div className="flex justify-center">
                <div className="grid grid-cols-2 gap-3 max-w-xs">
                  {[
                    { icon: <Heart className="w-6 h-6 text-white" />, label: "£1.00/month rate", sub: "Subject to verification" },
                    { icon: <Shield className="w-6 h-6 text-white" />, label: "Priority features", sub: "Early access" },
                    { icon: <Users className="w-6 h-6 text-white" />, label: "Family sharing", sub: "Cover your household" },
                    { icon: <Bell className="w-6 h-6 text-white" />, label: "Savings alerts", sub: "Community price alerts" },
                  ].map((card, i) => (
                    <motion.div
                      key={card.label}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 + 0.2 }}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10 hover:bg-white/15 transition-colors"
                    >
                      <div className="flex justify-center mb-2">{card.icon}</div>
                      <p className="text-xs font-bold text-white mb-0.5">{card.label}</p>
                      <p className="text-[10px] text-blue-200/70">{card.sub}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── S10: SOCIAL PROOF / TESTIMONIALS ── */}
      <section className="px-4 sm:px-6 lg:px-8 py-14 md:py-20 bg-white" data-testid="social-proof">
        <div className="max-w-5xl mx-auto">
          <Anim className="text-center mb-10">
            <p className="text-[11px] text-emerald-600 font-semibold uppercase tracking-wider mb-2">Real people, real savings</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>What our users say</h2>
          </Anim>

          {/* Individual testimonials — ASA-compliant.
              - Garry: named + specific savings — owner has provided written
                consent (he is the founder, and this is his own homepage).
              - Cards 2 & 3: anonymised. No name, no location, no specific £
                figure. Preserves 3-card layout without attributing
                unverified savings claims to unconsented individuals. */}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                name: "Garry",
                location: "London",
                text: "I used PricePAL Compare to check fuel nearby and found a cheaper station 2km away — saved £1.59 on that fill.",
                savings: "£1.59",
                feature: "Fuel Finder",
                anonymised: false
              },
              {
                name: "PricePAL user",
                location: "Verified",
                text: "Been scanning my weekly shop for a few weeks now. Switched a few items to cheaper retailers and it's added up.",
                savings: null,
                feature: "Price Compare",
                anonymised: true
              },
              {
                name: "PricePAL user",
                location: "Verified",
                text: "The fuel finder helped me spot a cheaper station on my usual route on my last fill.",
                savings: null,
                feature: "Fuel Finder",
                anonymised: true
              },
            ].map((t, i) => (
              <Anim key={i} delay={i * 0.08}>
                <div className="bg-zinc-50 rounded-2xl p-5 border border-zinc-100 h-full flex flex-col">
                  <div className="flex items-center gap-0.5 mb-3">
                    {[1,2,3,4,5].map(s => <Star key={s} className="w-3 h-3 text-amber-400 fill-amber-400" />)}
                  </div>
                  <p className="text-sm text-zinc-700 leading-relaxed mb-4 flex-1">"{t.text}"</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        {t.anonymised
                          ? <User className="w-4 h-4 text-emerald-700" />
                          : <span className="text-xs font-bold text-emerald-700">{t.name[0]}</span>}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-zinc-800">{t.name}</p>
                        <p className="text-[10px] text-zinc-400">{t.location}</p>
                      </div>
                    </div>
                    {t.savings && (
                      <div className="text-right">
                        <p className="text-xs font-black text-emerald-600">{t.savings}</p>
                        <p className="text-[9px] text-zinc-400">{t.feature}</p>
                      </div>
                    )}
                    {!t.savings && (
                      <div className="text-right">
                        <p className="text-[10px] text-zinc-400">{t.feature}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Anim>
            ))}
          </div>

          {/* Savings highlight strip — dynamic authoritative counts */}
          <Anim delay={0.25}>
            <div className="mt-8 grid grid-cols-3 sm:flex sm:flex-wrap sm:justify-center gap-2 sm:gap-3">
              <div className="bg-emerald-600 text-white rounded-xl px-3 sm:px-5 py-3 text-center">
                <p className="text-lg sm:text-xl font-black" style={{ fontFamily: "Outfit,sans-serif" }}>
                  <CountUp end={landingStats?.products || 7000} suffix="+" />
                </p>
                <p className="text-[9px] sm:text-[10px] font-medium text-emerald-200">products tracked</p>
              </div>
              <div className="bg-zinc-100 rounded-xl px-3 sm:px-5 py-3 text-center">
                <p className="text-lg sm:text-xl font-black text-zinc-800" style={{ fontFamily: "Outfit,sans-serif" }}>
                  {landingStats?.supermarkets ?? 6}
                </p>
                <p className="text-[9px] sm:text-[10px] font-medium text-zinc-400">supermarkets</p>
              </div>
              <div className="bg-zinc-100 rounded-xl px-3 sm:px-5 py-3 text-center flex items-center justify-center gap-1.5 sm:gap-2">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-400" />
                <div>
                  <p className="text-xs sm:text-sm font-black text-zinc-800" style={{ fontFamily: "Outfit,sans-serif" }}>&lt;5 sec</p>
                  <p className="text-[9px] sm:text-[10px] font-medium text-zinc-400">comparison</p>
                </div>
              </div>
            </div>
          </Anim>
        </div>
      </section>

      {/* ── S11: FINAL CTA + EMAIL CAPTURE ── */}
      <section className="px-4 sm:px-6 lg:px-8 py-14 md:py-20 bg-emerald-600" data-testid="final-cta">
        <div className="max-w-2xl mx-auto text-center">
          <Anim>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white leading-tight mb-3" style={{ fontFamily: "Outfit,sans-serif" }}>
              Ready to spend less on your next shop?
            </h2>
            <p className="text-sm sm:text-base text-emerald-100/80 mb-6">
              Compare available prices for supported products across UK supermarkets and find cheaper fuel nearby.
            </p>
            <Link to="/register" data-testid="final-cta-btn" onClick={() => handleCtaClick('final_cta', { position: 'footer', label: 'Start 7-Day Free Trial' })} className="block w-full sm:w-auto sm:inline-flex items-center justify-center gap-2 bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-8 py-4 rounded-full text-base text-center shadow-lg transition mb-3">
              Start 7-Day Free Trial <ArrowRight className="w-4 h-4 hidden sm:inline" />
            </Link>
            <p className="text-xs text-emerald-100/80 mb-6" data-testid="final-cta-support">
              Premium £1.99/month · Card required · Cancel any time before your trial ends and you won't be charged.
            </p>

            {/* Email capture */}
            <div className="bg-emerald-700/50 rounded-2xl p-5 backdrop-blur-sm border border-emerald-500/30">
              <p className="text-sm font-semibold text-emerald-100 mb-3">Not ready yet? Get weekly savings tips in your inbox.</p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={e => setNewsletterEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 px-4 py-3 rounded-full bg-white/10 border border-emerald-400/30 text-white placeholder:text-emerald-200/50 text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  data-testid="newsletter-email-input"
                  required
                />
                <button
                  type="submit"
                  disabled={newsletterStatus === "sending"}
                  className="px-5 py-3 bg-white text-emerald-700 font-bold rounded-full text-sm hover:bg-emerald-50 transition flex items-center justify-center gap-1.5 disabled:opacity-60"
                  data-testid="newsletter-submit-btn"
                >
                  <Send className="w-3.5 h-3.5" />
                  {newsletterStatus === "sending" ? "Sending..." : "Subscribe"}
                </button>
              </form>
              {newsletterStatus === "success" && <p className="text-xs text-emerald-200 mt-2 font-medium">You're in! Watch your inbox for savings tips.</p>}
              {newsletterStatus === "error" && <p className="text-xs text-red-200 mt-2 font-medium">Something went wrong. Please try again.</p>}
            </div>
          </Anim>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-zinc-900 text-zinc-400 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center"><span className="text-white font-black text-xs" style={{ fontFamily: "Outfit,sans-serif" }}>P</span></div>
                <span className="text-base font-bold text-white" style={{ fontFamily: "Outfit,sans-serif" }}>PricePAL Compare</span>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">
                UK price comparison app for groceries and fuel. Compare available retailer and community prices in one place, and find where products cost less.
              </p>
            </div>

            {/* Product */}
            <div>
              <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">Product</p>
              <nav className="flex flex-col gap-2">
                {[
                  { label: "Price Compare", href: "/app?view=compare-parent" },
                  { label: "Fuel Finder", href: "/app?view=fuel-prices" },
                  { label: "Kitchen AI", href: "/app?view=kitchen" },
                  { label: "Pricing", href: "/pricing" },
                ].map(l => <Link key={l.label} to={l.href} className="text-sm text-zinc-500 hover:text-emerald-400 transition">{l.label}</Link>)}
              </nav>
            </div>

            {/* Company */}
            <div>
              <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">Company</p>
              <nav className="flex flex-col gap-2">
                {[
                  { label: "About", href: "/about" },
                  { label: "Contact", href: "/contact" },
                  { label: "Community", href: "/community" },
                ].map(l => <Link key={l.label} to={l.href} className="text-sm text-zinc-500 hover:text-emerald-400 transition">{l.label}</Link>)}
              </nav>
            </div>

            {/* Legal */}
            <div>
              <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">Legal</p>
              <nav className="flex flex-col gap-2">
                {[
                  { label: "Privacy Policy", href: "/privacy" },
                  { label: "Terms of Service", href: "/terms" },
                  { label: "Cookie Policy", href: "/cookies" },
                ].map(l => <Link key={l.label} to={l.href} className="text-sm text-zinc-500 hover:text-emerald-400 transition">{l.label}</Link>)}
              </nav>
            </div>
          </div>

          {/* PWA Install hint */}
          <div className="border-t border-zinc-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-zinc-800 rounded-lg px-3 py-2 flex items-center gap-2">
                <div className="w-5 h-5 rounded bg-emerald-600 flex items-center justify-center">
                  <span className="text-white font-black text-[8px]" style={{ fontFamily: "Outfit,sans-serif" }}>P</span>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-zinc-300">Add to Home Screen</p>
                  <p className="text-[9px] text-zinc-500">Install as an app on your phone</p>
                </div>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-xs text-zinc-500 max-w-md sm:ml-auto">Prices shown are for comparison only. Availability and prices can change and should be confirmed at the retailer or fuel station before purchase.</p>
              <p className="text-xs text-zinc-600 mt-0.5">&copy; 2026 PricePAL Compare Ltd. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* ── STICKY MOBILE CTA ── */}
      <div className="fixed bottom-0 left-0 w-full p-3 bg-white/95 backdrop-blur-md border-t border-zinc-200 md:hidden z-[100]" data-testid="sticky-mobile-cta">
        <Link to="/register" onClick={() => handleCtaClick('sticky_mobile_cta', { position: 'sticky_mobile_footer', label: 'Start 7-Day Free Trial' })} data-testid="sticky-mobile-cta-link" className="block w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-3.5 font-bold text-base text-center shadow-md transition">
          Start 7-Day Free Trial
        </Link>
      </div>
      <div className="h-24 md:hidden" />
    </div>
  );
}
